import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search } from "lucide-react";
import { SmartSearch, type SearchHit } from "@/lib/smartSearch";
import { facetOf, type FacetId } from "@/components/search/SmartSearchPanel";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";

const FACETS: { id: FacetId; label: string }[] = [
  { id: "all", label: "All" },
  { id: "inventory", label: "Unit Inventory" },
  { id: "guide", label: "Guides" },
  { id: "blog", label: "Blog" },
  { id: "page", label: "General Pages" },
];

const PLACEHOLDER = "/placeholder.svg";

export default function SiteSearch() {
  const search = useSearch({ from: "/search" }) as { q?: string; facet?: string };
  const navigate = useNavigate();
  const q = search.q ?? "";
  const [input, setInput] = useState(q);
  const [hits, setHits] = useState<SearchHit[]>([]);
  // Start in the loading state whenever there is a searchable query so the
  // first paint (SSR + pre-index-load) never flashes an empty "No matches".
  const [loading, setLoading] = useState(() => q.trim().length >= 2);
  const [facet, setFacet] = useState<FacetId>((search.facet as FacetId) ?? "all");
  const [images, setImages] = useState<Record<string, string>>({});

  useEffect(() => setInput(q), [q]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    SmartSearch.search(q, { limit: 200 })
      .then((r) => alive && setHits(r.hits))
      .catch(() => alive && setHits([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [q]);

  // Pull first image per unit for the inventory cards.
  useEffect(() => {
    const stocks = [
      ...new Set(
        hits
          .filter((h) => h.doc.source === "inventory" && h.doc.stockNumber)
          .map((h) => h.doc.stockNumber as string),
      ),
    ].slice(0, 100);
    if (stocks.length === 0) return;
    let alive = true;
    void (async () => {
      const { data } = await supabase
        .from("rv_inventory")
        .select("stock_number, images")
        .in("stock_number", stocks);
      if (!alive || !data) return;
      const map: Record<string, string> = {};
      for (const row of data as { stock_number: string | null; images: string[] | null }[]) {
        const first = row.images?.[0];
        if (row.stock_number && first) map[row.stock_number] = first;
      }
      setImages(map);
    })();
    return () => {
      alive = false;
    };
  }, [hits]);

  const counts = useMemo(() => {
    const c: Record<FacetId, number> = { all: hits.length, inventory: 0, guide: 0, blog: 0, page: 0 };
    for (const h of hits) c[facetOf(h)] += 1;
    return c;
  }, [hits]);

  const filtered = useMemo(
    () => (facet === "all" ? hits : hits.filter((h) => facetOf(h) === facet)),
    [hits, facet],
  );

  function submit(e: React.FormEvent) {
    e.preventDefault();
    void navigate({ to: "/search", search: { q: input, facet: undefined } });
  }

  return (
    <Layout>
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold">Search results</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {loading ? "Searching…" : `${hits.length} result${hits.length === 1 ? "" : "s"}`}
        {q ? ` for “${q}”` : ""}
      </p>

      <form onSubmit={submit} className="relative mt-5 max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search units, guides and pages"
        />
      </form>

      <div className="mt-5 flex flex-wrap gap-2">
        {FACETS.map((f) => {
          const n = counts[f.id];
          const active = facet === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFacet(f.id)}
              disabled={f.id !== "all" && n === 0}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 ${
                active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              {f.label}
              <span className="ml-1.5 opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading results…
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-16 text-muted-foreground">
          No matches{q ? <> for “{q}”</> : null}. Try a different wording.
        </p>
      ) : (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((hit) => {
            const d = hit.doc;
            const img = (d.stockNumber ? images[d.stockNumber] : undefined) ?? d.image ?? undefined;
            return (
              <Link
                key={d.id}
                to={d.path}
                className="group overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow hover:shadow-lg"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                  <img
                    src={img ?? PLACEHOLDER}
                    alt={d.title}
                    loading="lazy"
                    width={400}
                    height={300}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = PLACEHOLDER;
                    }}
                  />
                  {d.condition ? (
                    <Badge className="absolute left-2 top-2 capitalize">{d.condition}</Badge>
                  ) : null}
                </div>
                <div className="space-y-1 p-4">
                  <h2 className="line-clamp-2 text-sm font-bold leading-snug">{d.title}</h2>
                  {d.price ? (
                    <p className="text-base font-bold text-primary">${d.price.toLocaleString()}</p>
                  ) : null}
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {[d.manufacturer ?? d.brand, d.rvType, d.stockNumber ? `Stock #${d.stockNumber}` : null]
                      .filter(Boolean)
                      .join(" · ") || d.summary || d.type}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
    </Layout>
  );
}
